// This file is a extension of the turndown library to support GitHub Flavored Markdown (GFM) with tables.
// My biggest conern is that this code is not well maintaned, and due to security reasons i wanted to copy it directly into code + add Table support.

import type TurndownService from 'turndown';

const alignMap: { [key: string]: string } = {
  left: ':--',
  right: '--:',
  center: ':-:',
};

function isHeadingRow(tr: HTMLElement): boolean {
  const parentNode = tr.parentNode as HTMLElement;
  const tableNode =
    parentNode.nodeName === 'THEAD' ||
    parentNode.nodeName === 'TFOOT' ||
    parentNode.nodeName === 'TBODY'
      ? (parentNode.parentNode as HTMLElement)
      : parentNode;

  return tableNode.nodeName === 'TABLE' && (tableNode as HTMLTableElement).rows[0] === tr;
}

function isNestedTable(tableNode: HTMLElement): boolean {
  let currentNode = tableNode.parentNode;

  while (currentNode) {
    if (currentNode.nodeName === 'TABLE') return true;
    currentNode = currentNode.parentNode;
  }

  return false;
}

function cell(content: string, node: HTMLElement): string {
  const index = Array.from(node.parentNode?.childNodes ?? []).indexOf(node);
  const prefix = index === 0 ? '| ' : ' ';
  const value = content.replace(/\r\n/g, '\n').replace(/\n/g, ' ').replace(/\|/g, '\\|');

  return prefix + value + ' |';
}

function spannedCells(node: HTMLElement, spannedCellContent: string): string {
  const colspan = Number(node.getAttribute('colspan') ?? 1);

  if (colspan <= 1) return '';

  return ` ${spannedCellContent} |`.repeat(colspan - 1);
}

const rules: { [key: string]: TurndownService.Rule } = {
  tableCell: {
    filter: ['th', 'td'],
    replacement: (content, node) => cell(content, node) + spannedCells(node, ''),
  },
  tableRow: {
    filter: 'tr',
    replacement: (content, node) => {
      let borderCells = '';

      if (isHeadingRow(node)) {
        node.childNodes.forEach(childNode => {
          const cellNode = childNode as HTMLElement;
          const align = (cellNode.getAttribute('align') ?? '').toLowerCase();
          const border = align ? alignMap[align] ?? '---' : '---';

          borderCells += cell(border, cellNode) + spannedCells(cellNode, border);
        });
      }

      return `\n${content}${borderCells ? `\n${borderCells}` : ''}`;
    },
  },
  table: {
    filter: node => node.nodeName === 'TABLE' && !isNestedTable(node),
    replacement: content => `\n\n${content.replace('\n\n', '\n')}\n\n`,
  },
  tableSection: {
    filter: ['thead', 'tbody', 'tfoot'],
    replacement: content => content,
  },
  captionSection: {
    filter: 'caption',
    replacement: (content, node) => {
      const parent = node.parentNode;

      return parent?.nodeName === 'TABLE' && parent.childNodes[0] === node ? content : '';
    },
  },
};

export const turndownGfmTables: TurndownService.Plugin = turndownService => {
  turndownService.keep(node => node.nodeName === 'TABLE' && isNestedTable(node));

  Object.entries(rules).forEach(([key, rule]) => turndownService.addRule(key, rule));
};
